import { currentKp } from '../utils/observations.js';
import { useState, useEffect, useRef } from 'react';
import SatelliteCard from './SatelliteCard.jsx';
import DatasheetCard from './DatasheetCard.jsx';
import SolarObjectCard from './SolarObjectCard.jsx';

const MONO = 'var(--mono)';
const C = {
  wrap: {
    width: '100%', height: '100%',
    background: 'var(--bg)',
    borderLeft: '1px solid var(--hair)',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden', flexShrink: 0,
  },
  section: {
    borderBottom: '1px solid var(--hair)',
    padding: '8px 10px', flexShrink: 0,
  },
  sLabel: {
    color: 'var(--txt-dim)', fontSize: 'var(--ui-label)',
    letterSpacing: '0.16em', fontFamily: MONO,
    marginBottom: 6, textTransform: 'uppercase', fontWeight: 700,
    borderBottom: '1px solid var(--hair)', paddingBottom: 5,
  },
  row: { display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' },
  dot: (color, glow) => ({
    width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0,
    boxShadow: glow ? `0 0 6px ${color}` : 'none',
  }),
  key:     { color: 'var(--txt-dim)', fontSize: 'var(--ui-body)', fontFamily: MONO, flex: 1 },
  val:     { color: 'var(--txt)', fontSize: 'var(--ui-body)', fontFamily: MONO, fontWeight: 500 },
  valHi:   { color: 'var(--txt-bright)', fontSize: 'var(--ui-body)', fontFamily: MONO, fontWeight: 700 },
  valWarn: { color: 'var(--accent)', fontSize: 'var(--ui-body)', fontFamily: MONO, fontWeight: 600 },
  valCrit: { color: 'var(--red)', fontSize: 'var(--ui-body)', fontFamily: MONO, fontWeight: 600 },
  hint: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--txt-dim)', fontSize: 'var(--ui-body)', fontFamily: MONO, letterSpacing: '0.06em',
    textAlign: 'center', padding: 16, lineHeight: 1.8, fontWeight: 500,
  },
  satWrap: { flex: 1, overflowY: 'auto', position: 'relative' },
};

function domainDot(count, threshWarn, threshCrit) {
  if (count >= threshCrit) return { color: 'var(--red)', glow: true };
  if (count >= threshWarn) return { color: 'var(--warn)', glow: false };
  return { color: 'var(--green)', glow: false };
}

export default function RightConsole({ selected, tracked, observer, onFollow, onUntrack, onClose,
                                       weather, conjunctions, reentries, satellites,
                                       solarSelected, onSolarClose }) {
  // Animate content swap when selected changes
  const [visible, setVisible] = useState(true);
  const prevSelected = useRef(selected);
  useEffect(() => {
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches){setVisible(true);prevSelected.current=selected;return;}
    if (prevSelected.current?.norad_id !== selected?.norad_id) {
      setVisible(false);
      const t = setTimeout(() => { setVisible(true); prevSelected.current = selected; }, 120);
      return () => clearTimeout(t);
    }
  }, [selected]);

  const conjCount  = (conjunctions || []).length;
  const rCount     = (reentries || []).filter(r => r.decay_epoch && new Date(r.decay_epoch) > new Date()).length;
  const nearestConj = conjCount > 0
    ? conjunctions.find(c => c.tca && new Date(c.tca) > new Date())
    : null;
  const nearestReentry = rCount > 0
    ? reentries.filter(r => r.decay_epoch && new Date(r.decay_epoch) > new Date())
               .sort((a,b) => new Date(a.decay_epoch) - new Date(b.decay_epoch))[0]
    : null;

  const kp = currentKp(weather);
  const stormLabel = weather?.kp?.level?.toUpperCase() || 'QUIET';
  const wxDot = kp == null ? { color: 'var(--txt-mute)', glow: false } : kp >= 5 ? { color: 'var(--warn)', glow: true } : { color: 'var(--green)', glow: false };
  const conjDot = domainDot(conjCount, 1, 3);
  const rDot = domainDot(rCount, 1, 3);

  const hoursUntil = (dateStr) => {
    if (!dateStr) return null;
    const h = (new Date(dateStr) - Date.now()) / 3600000;
    if (h < 0) return null;
    if (h < 1) return `${Math.round(h*60)}m`;
    return `${h.toFixed(1)}h`;
  };

  // When an object is selected, show the appropriate card filling the console.
  // Special types (aviation, groundstation, launches) keep SatelliteCard which
  // handles their unique layouts. Real orbital objects get DatasheetCard with
  // full provenance, curated name, AI summary, live telemetry, and pass prediction.
  // Solar system object selected � show SolarObjectCard
  if (solarSelected) {
    return <SolarObjectCard item={solarSelected} onClose={onSolarClose} />;
  }

  if (selected) {
    const isSpecial = ['aviation', 'groundstation', 'launches'].includes(selected.category);
    if (isSpecial) {
      return (
        <div style={C.wrap}>
          <div style={{ ...C.satWrap, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(6px)", transition: "opacity 0.15s ease, transform 0.15s ease" }}>
            <SatelliteCard
              satellite={selected}
              tracked={tracked === selected?.norad_id}
              observer={observer}
              onFollow={() => onFollow(selected)}
              onUntrack={onUntrack}
              onClose={onClose}
              compact={true}
            />
          </div>
        </div>
      );
    }
    // Real orbital object, DatasheetCard in panel mode
    return (
      <div style={C.wrap}>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(6px)", transition: "opacity 0.15s ease, transform 0.15s ease" }}>
        <DatasheetCard
          norad={selected.norad_id}
          panelMode={true}
          sat={selected}
          tracked={tracked === selected?.norad_id}
          observer={observer}
          onFollow={onFollow}
          onUntrack={onUntrack}
          onCollapse={onClose}
        />
        </div>
      </div>
    );
  }

  // Default: domain status console
  return (
    <div style={C.wrap}>

      <div style={C.section}>
        <div style={C.sLabel}>Domain Status</div>

        <div style={C.row}>
          <div style={C.dot(wxDot.color, wxDot.glow)} />
          <span style={C.key}>Space Wx</span>
          <span style={kp >= 5 ? C.valWarn : C.val}>
            {kp >= 5 ? stormLabel : `Kp ${(kp == null ? '-' : kp.toFixed(1))}`}
          </span>
        </div>

        <div style={C.row}>
          <div style={C.dot(conjDot.color, conjDot.glow)} />
          <span style={C.key}>Conjunct</span>
          <span style={conjCount > 0 ? C.valWarn : C.val}>
            {conjCount > 0 ? `${conjCount} active` : 'clear'}
          </span>
        </div>
        {nearestConj && (
          <div style={{ ...C.row, paddingLeft: 14 }}>
            <span style={{ ...C.key, color: 'var(--txt-dim)', fontSize: 'var(--ui-label)' }}>TCA</span>
            <span style={{ ...C.valWarn, fontSize: 'var(--ui-label)' }}>
              {hoursUntil(nearestConj.tca) || '-'}
            </span>
          </div>
        )}

        <div style={C.row}>
          <div style={C.dot(rDot.color, rDot.glow)} />
          <span style={C.key}>Reentry</span>
          <span style={rCount > 0 ? C.valWarn : C.val}>
            {rCount > 0 ? `${rCount} active` : 'clear'}
          </span>
        </div>
        {nearestReentry && (
          <div style={{ ...C.row, paddingLeft: 14 }}>
            <span style={{ ...C.key, color: 'var(--txt-dim)', fontSize: 'var(--ui-label)' }}>ETA</span>
            <span style={{ ...C.valWarn, fontSize: 'var(--ui-label)' }}>
              {hoursUntil(nearestReentry.decay_epoch) || '-'}
            </span>
          </div>
        )}

      </div>

      <div style={C.hint}>
        Click any object<br />on the globe<br />to inspect it.
      </div>
    </div>
  );
}
